import { useState, useEffect } from "react";
import { getEntityNewsForEntityApi } from "../api/entityNews.api";
import usePermissions from "./usePermissions";
import { getFeatures } from "../libs/features";

/**
 * Custom hook to fetch entity news.
 * Automatically checks for entityNews feature enablement and user permissions.
 * @param {string} entityType - The type of entity (e.g., 'company').
 * @param {string} entityId - The ID of the entity.
 * @param {boolean} enabled - Optional additional toggle (defaults to true).
 */
const useEntityNews = (entityType, entityId, enabled = true) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const permissions = usePermissions();
  const features = getFeatures();

  const isActuallyEnabled =
    enabled && features.entityNews && permissions.entityNews;

  useEffect(() => {
    if (!isActuallyEnabled || !entityType || !entityId) {
      setData([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getEntityNewsForEntityApi({
          entityType,
          entityId,
        });
        setData(response.data || []);
      } catch (err) {
        console.error(`Error loading news for ${entityType} ${entityId}:`, err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [entityType, entityId, isActuallyEnabled]);

  return { data, loading, error };
};

export default useEntityNews;
